// Profile — the classic per-user page: avatar, name, email, and a
// self-service password change. Agency-wide settings live on
// /settings/agency instead.
import * as React from 'react';
import { KeyRound, Loader2, UserCircle } from 'lucide-react';
import { useAuth } from '@proptr/api-client/react';
import { AppShell } from '../components/layout/AppShell.js';
import { ImageUploadButton } from '../components/ImageUploadButton.js';
import { SectionCard } from '../components/SectionCard.js';
import { Button } from '../components/ui/button.js';
import { Input } from '../components/ui/input.js';
import { Label } from '../components/ui/label.js';
import { initials } from '../lib/utils.js';
import { client } from '../main.js';

export function ProfilePage() {
  const { user, refresh } = useAuth();

  return (
    <AppShell>
      <div className="mx-auto max-w-[720px] space-y-6 pb-10">
        <div>
          <h1 className="font-display text-[40px] font-medium leading-none tracking-[-0.02em]">
            Profile
          </h1>
          <p className="mt-3 font-mono text-[11px] text-muted-foreground">
            Your personal account. Agency-wide branding lives under Settings.
          </p>
        </div>

        <SectionCard
          title="Your details"
          subtitle="Avatar, name, and the email you sign in with"
          icon={UserCircle}
          collapsible={false}
        >
          <DetailsForm
            key={user?._id ?? 'anon'}
            initialName={user?.name ?? ''}
            initialImage={(user as { image?: string | null } | null)?.image ?? null}
            email={user?.email ?? ''}
            onSaved={refresh}
          />
        </SectionCard>

        <SectionCard
          title="Password"
          subtitle="Change the password you sign in with"
          icon={KeyRound}
          collapsible={false}
        >
          <PasswordForm />
        </SectionCard>
      </div>
    </AppShell>
  );
}

function DetailsForm({
  initialName,
  initialImage,
  email,
  onSaved,
}: {
  initialName: string;
  initialImage: string | null;
  email: string;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = React.useState(initialName);
  const [image, setImage] = React.useState<string | null>(initialImage);
  const [saving, setSaving] = React.useState(false);
  const [saved, setSaved] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const dirty = name !== initialName || image !== initialImage;

  const save = async () => {
    if (saving) return;
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      await client.me.updateProfile({ name: name.trim() || undefined, image });
      await onSaved();
      setSaved(true);
    } catch (err) {
      setError((err as Error).message || 'Could not save — try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 p-5">
      <div className="flex items-center gap-4">
        <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border border-border bg-muted">
          {image ? (
            <img src={image} alt="Your avatar" className="h-full w-full object-cover" />
          ) : (
            <span className="font-mono text-[16px] font-bold uppercase text-muted-foreground">
              {initials(name || email || 'U')}
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ImageUploadButton
            kind="user-avatar"
            label="Change picture"
            onUploaded={(url) => setImage(url)}
          />
          {image && (
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setImage(null)}
            >
              Remove
            </Button>
          )}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label htmlFor="pf-name" className="eyebrow">
            Name
          </Label>
          <Input
            id="pf-name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setSaved(false);
            }}
            placeholder="Your name"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pf-email" className="eyebrow">
            Email
          </Label>
          <Input id="pf-email" value={email} disabled readOnly />
          <p className="font-mono text-[10px] text-muted-foreground">
            Your sign-in email can't be changed here.
          </p>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-[11px] text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {saved && !dirty && (
          <span className="font-mono text-[11px] text-success">Saved.</span>
        )}
        <Button size="sm" onClick={() => void save()} disabled={saving || !dirty}>
          {saving ? <Loader2 className="animate-spin" /> : null}
          Save changes
        </Button>
      </div>
    </div>
  );
}

function PasswordForm() {
  const [current, setCurrent] = React.useState('');
  const [next, setNext] = React.useState('');
  const [confirm, setConfirm] = React.useState('');
  const [saving, setSaving] = React.useState(false);
  const [done, setDone] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (saving) return;
    setError(null);
    setDone(false);
    if (next.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }
    if (next !== confirm) {
      setError('New passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await client.me.changePassword({
        currentPassword: current,
        newPassword: next,
      });
      setDone(true);
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (err) {
      setError((err as Error).message || 'Could not update password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form className="space-y-4 p-5" onSubmit={submit} noValidate>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="space-y-1.5">
          <Label htmlFor="pw-current" className="eyebrow">
            Current password
          </Label>
          <Input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw-new" className="eyebrow">
            New password
          </Label>
          <Input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            placeholder="At least 8 characters"
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pw-confirm" className="eyebrow">
            Repeat new password
          </Label>
          <Input
            id="pw-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 font-mono text-[11px] text-destructive">
          {error}
        </div>
      )}

      <div className="flex items-center justify-end gap-3">
        {done && (
          <span className="font-mono text-[11px] text-success">
            Password updated.
          </span>
        )}
        <Button
          size="sm"
          type="submit"
          disabled={saving || !current || !next || !confirm}
        >
          {saving ? <Loader2 className="animate-spin" /> : null}
          Update password
        </Button>
      </div>
    </form>
  );
}
